// How an address reads to a person, in one place (DMS-47).
//
// Since the DMS stores addresses by parts, `text` is empty on every row that
// has been split and carries the whole line only on one that has not. A screen
// that reads `text` straight shows nothing at all for a new delivery, so every
// screen goes through here instead.
//
// This mirrors `customer.ReadableLine` in card-dms-core, minus the zone: the
// zone travels as its own field and the screens that want it append it
// themselves, so composing it in here would print it twice.

/** The shape every feature's address satisfies; each has its own entity. */
export interface ReadableAddress {
  /** The legacy single line. Null or empty once the row has been split. */
  text?: string | null;
  street?: string | null;
  number?: string | null;
  neighborhood?: string | null;
}

const clean = (v: string | null | undefined): string => (v ?? "").trim();

/**
 * The legacy line when the row still has one, otherwise the parts composed as
 * "<street> <number>, <neighborhood>". Empty pieces are skipped, so a row with
 * only a street reads as that street.
 *
 * Returns "" when there is nothing to show — an approval that arrived without
 * an address (plan §11, D18). Callers decide what to print in its place.
 */
export const addressLine = (a: ReadableAddress | null | undefined): string => {
  if (!a) return "";
  const legacy = clean(a.text);
  if (legacy) return legacy;
  const streetAndNumber = [clean(a.street), clean(a.number)].filter(Boolean).join(" ");
  return [streetAndNumber, clean(a.neighborhood)].filter(Boolean).join(", ");
};

/** True when the address carries nothing to show. */
export const hasNoAddress = (a: ReadableAddress | null | undefined): boolean =>
  addressLine(a) === "";
